import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { mapboxgl, MAPBOX_STYLE, MAPBOX_TOKEN, BRAND_BLUE, BRAND_ORANGE } from "@/lib/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import { Layout } from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { MagnifyingGlass, MapPin, ArrowRight, SealCheck, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

function QuoteFormModal({ listing, zip, onClose }) {
  const [form, setForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    job_type: "car", message: "", preferred_contact: "email",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/finder/leads", { listing_id: listing?.id || null, zip: zip || "", ...form });
      setDone(true);
      toast.success("Request sent");
    } catch (err) { toast.error(formatApiError(err)); }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="quote-modal">
      <div className="surface max-w-lg w-full p-8" onClick={(e) => e.stopPropagation()}>
        <div className="section-label mb-2">— REQUEST A QUOTE</div>
        <h3 className="font-chivo font-black text-2xl text-white uppercase tracking-tight mb-1" data-testid="quote-modal-title">
          {listing ? listing.business_name : `No pro near ${zip}`}
        </h3>
        <p className="font-plex text-xs text-[#9CA3AF] mb-5">
          {listing ? "Your request goes directly to this contractor. No middleman, no fee."
                   : "We don't have a verified pro in your area yet. Share your request and we'll match you when one joins."}
        </p>
        {done ? (
          <div className="text-center py-6">
            <SealCheck size={42} weight="fill" className="text-[#00D4FF] mx-auto mb-3" />
            <div className="font-chivo font-bold text-white uppercase tracking-tight">Request sent</div>
            <p className="font-plex text-sm text-[#9CA3AF] mt-2">
              {listing ? "The contractor will reply directly. Check your email for a copy." : "We'll email you when a contractor in your area joins."}
            </p>
            <button onClick={onClose} className="btn-secondary mt-6" data-testid="quote-close-btn">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="quote-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input required placeholder="Your name" value={form.customer_name} onChange={onChange("customer_name")} className="input-rugged" data-testid="quote-name" />
              <input placeholder="Phone" value={form.customer_phone} onChange={onChange("customer_phone")} className="input-rugged" data-testid="quote-phone" />
            </div>
            <input type="email" required placeholder="Email" value={form.customer_email} onChange={onChange("customer_email")} className="input-rugged" data-testid="quote-email" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <select value={form.job_type} onChange={onChange("job_type")} className="input-rugged" data-testid="quote-job-type">
                <option value="car">Car / Detailing</option>
                <option value="industrial">Industrial / Machine</option>
                <option value="marine">Marine</option>
                <option value="restoration">Fire / Smoke / Mold</option>
                <option value="other">Other</option>
              </select>
              <select value={form.preferred_contact} onChange={onChange("preferred_contact")} className="input-rugged" data-testid="quote-contact-pref">
                <option value="email">Email me</option>
                <option value="phone">Call me</option>
                <option value="either">Either is fine</option>
              </select>
            </div>
            <textarea required placeholder="Describe the job: surface, size, timing, any photos you can share later." rows={4} value={form.message} onChange={onChange("message")} className="input-rugged" data-testid="quote-message" />
            <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="quote-submit-btn">
              {loading ? "Sending..." : "Send Request"}
            </button>
            <button type="button" onClick={onClose} className="font-mono text-xs text-[#52525B] hover:text-[#9CA3AF] block mx-auto" data-testid="quote-cancel-btn">Cancel</button>
          </form>
        )}
      </div>
    </div>
  );
}

export default function Find() {
  const [params, setParams] = useSearchParams();
  const initialZip = params.get("zip") || "";
  const [zipInput, setZipInput] = useState(initialZip);
  const [zip, setZip] = useState(initialZip);
  const [radius, setRadius] = useState(Number(params.get("radius")) || 50);
  const [serviceType, setServiceType] = useState(params.get("service_type") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [quoteFor, setQuoteFor] = useState(null);

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const originMarkerRef = useRef(null);

  const center = useMemo(() => {
    if (data?.origin?.lat && data?.origin?.lng) return [data.origin.lng, data.origin.lat];
    return [-98.5795, 39.8283];
  }, [data]);

  // Initialize map ONCE
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    if (!MAPBOX_TOKEN) {
      console.warn("Missing REACT_APP_MAPBOX_TOKEN");
      return;
    }
    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: MAPBOX_STYLE,
      center,
      zoom: data?.origin ? 9 : 3.5,
      attributionControl: false,
    });
    mapRef.current.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
    mapRef.current.addControl(new mapboxgl.AttributionControl({ compact: true }), "bottom-right");
    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line
  }, []);

  // Update markers + recenter when data changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !data) return;
    // clear markers
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];
    originMarkerRef.current?.remove();
    originMarkerRef.current = null;

    // origin marker (orange)
    if (data.origin?.lat && data.origin?.lng) {
      const el = document.createElement("div");
      el.style.cssText = `width:14px;height:14px;border-radius:50%;background:${BRAND_ORANGE};border:3px solid #0A0A0A;box-shadow:0 0 0 2px ${BRAND_ORANGE}`;
      originMarkerRef.current = new mapboxgl.Marker({ element: el })
        .setLngLat([data.origin.lng, data.origin.lat])
        .setPopup(new mapboxgl.Popup({ offset: 16 }).setHTML(`<div style="font-family:monospace;color:#000">You searched from ${data.origin.zip}</div>`))
        .addTo(map);
    }

    // listing pins (cyan)
    (data.listings || []).forEach((l) => {
      if (!l.lat || !l.lng) return;
      const el = document.createElement("div");
      el.setAttribute("data-testid", `map-pin-${l.slug}`);
      el.style.cssText = `width:24px;height:24px;border-radius:50%;background:${BRAND_BLUE};border:3px solid #0A0A0A;box-shadow:0 0 0 2px ${BRAND_BLUE},0 4px 10px rgba(0,0,0,.5);cursor:pointer;transition:transform .15s`;
      el.onmouseenter = () => (el.style.transform = "scale(1.2)");
      el.onmouseleave = () => (el.style.transform = "scale(1)");
      const popupHtml = `
        <div style="font-family:'IBM Plex Sans',sans-serif;min-width:200px;color:#0A0A0A">
          <div style="font-weight:700;font-size:14px">${l.business_name}</div>
          <div style="font-size:11px;color:#525252;margin-bottom:6px">${l.city}, ${l.state} · ${l.distance_mi} mi</div>
          <a href="/pro/${l.slug}" style="color:${BRAND_BLUE};text-decoration:underline;font-size:12px;font-weight:600">View profile</a>
        </div>`;
      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat([l.lng, l.lat])
        .setPopup(new mapboxgl.Popup({ offset: 16, closeButton: false }).setHTML(popupHtml))
        .addTo(map);
      markersRef.current.push(marker);
    });

    // Recenter
    if (data.origin?.lat && data.origin?.lng) {
      map.flyTo({ center: [data.origin.lng, data.origin.lat], zoom: data.listings?.length ? 9 : 10, duration: 1200 });
    }
  }, [data]);

  const runSearch = async (z = zip, r = radius, st = serviceType) => {
    if (!z || z.length !== 5) return;
    setLoading(true); setErr("");
    try {
      const qp = new URLSearchParams({ zip: z, radius: String(r) });
      if (st) qp.set("service_type", st);
      const { data } = await api.get(`/finder/search?${qp.toString()}`);
      setData(data);
      setParams({ zip: z, radius: String(r), ...(st ? { service_type: st } : {}) });
    } catch (e) {
      setErr(formatApiError(e));
      setData(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (initialZip && initialZip.length === 5) runSearch(initialZip, radius, serviceType);
    // eslint-disable-next-line
  }, []);

  const onSearch = (e) => { e?.preventDefault?.(); setZip(zipInput); runSearch(zipInput, radius, serviceType); };
  const listings = data?.listings || [];
  const noneFound = data && listings.length === 0;

  return (
    <Layout>
      <section className="border-b border-[#262626] bg-[#0A0A0A]">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-10">
          <div className="section-label mb-3">— FIND A BLASTER</div>
          <h1 className="font-chivo font-black text-3xl sm:text-4xl lg:text-5xl text-white uppercase tracking-tighter" data-testid="find-headline">
            Dry ice pros near <span className="text-[#00D4FF]">{data?.origin?.zip || "you"}</span>.
          </h1>
          <form onSubmit={onSearch} className="mt-6 flex flex-col sm:flex-row items-stretch gap-3" data-testid="find-search-form">
            <div className="flex items-center gap-3 input-rugged py-3 flex-1 min-w-0">
              <MapPin size={18} weight="duotone" className="text-[#00D4FF] shrink-0" />
              <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={5}
                placeholder="US Zip (e.g. 75201)" value={zipInput}
                onChange={(e) => setZipInput(e.target.value.replace(/\D/g, ""))}
                className="flex-1 bg-transparent outline-none text-white min-w-0"
                data-testid="find-zip-input" />
            </div>
            <select value={radius} onChange={(e) => setRadius(Number(e.target.value))} className="input-rugged sm:w-28" data-testid="find-radius">
              <option value={25}>25 mi</option><option value={50}>50 mi</option><option value={100}>100 mi</option><option value={250}>250 mi</option>
            </select>
            <select value={serviceType} onChange={(e) => setServiceType(e.target.value)} className="input-rugged sm:w-44" data-testid="find-service-type">
              <option value="">Any service</option><option value="car">Car / Detailing</option><option value="industrial">Industrial</option>
            </select>
            <button type="submit" className="btn-primary sm:w-40 whitespace-nowrap" data-testid="find-submit">
              <MagnifyingGlass size={16} weight="bold" /> {loading ? "Searching..." : "Search"}
            </button>
          </form>
          {err && <div className="font-mono text-xs text-[#EF4444] mt-3" data-testid="find-error">{err}</div>}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 h-[560px] border border-[#262626]" data-testid="find-map">
          <div ref={mapContainerRef} style={{ width: "100%", height: "100%", background: "#0A0A0A" }} />
        </div>

        <div className="lg:col-span-5 space-y-3" data-testid="find-results">
          {!data && !loading && (
            <div className="surface p-8 text-center">
              <MapPin size={36} weight="duotone" className="text-[#00D4FF] mx-auto mb-3" />
              <div className="font-chivo font-bold text-white uppercase tracking-tight">Enter a zip to see pros</div>
              <p className="font-plex text-sm text-[#9CA3AF] mt-2">Try 90001 (LA), 75201 (Dallas), 60601 (Chicago), 33101 (Miami) or 15213 (Pittsburgh) for the live demo.</p>
            </div>
          )}
          {noneFound && (
            <div className="surface p-6 border-[#FF5722]/40" data-testid="find-no-results">
              <div className="flex items-center gap-2 mb-2">
                <Warning size={18} weight="fill" className="text-[#FF5722]" />
                <span className="font-chivo font-bold text-white uppercase tracking-tight">No pros in range</span>
              </div>
              <p className="font-plex text-sm text-[#9CA3AF] mb-4">
                We don't have a verified contractor within {radius} miles of {data.origin.zip} yet. Share your project and we'll match you when one joins.
              </p>
              <button onClick={() => setQuoteFor({ unmatched: true })} className="btn-primary w-full" data-testid="unmatched-cta">
                Request a Quote Anyway
              </button>
            </div>
          )}
          {listings.map((l) => (
            <div key={l.id} className="surface p-5" data-testid={`result-card-${l.slug}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-chivo font-bold text-white uppercase tracking-tight truncate">{l.business_name}</h3>
                    {l.verified && <SealCheck size={16} weight="fill" className="text-[#00D4FF] shrink-0" title="Verified" />}
                  </div>
                  <div className="font-mono text-xs text-[#52525B]">{l.city}, {l.state} · {l.distance_mi} mi</div>
                </div>
                <div className="text-right font-mono text-[11px] text-[#9CA3AF] whitespace-nowrap">
                  {l.service_style?.map((s) => s === "mobile" ? "Mobile" : "In-shop").join(" · ")}
                </div>
              </div>
              <p className="font-plex text-sm text-[#9CA3AF] mt-2 line-clamp-2">{l.about}</p>
              <div className="flex flex-wrap gap-1 mt-3">
                {(l.service_types || []).map((t) => (
                  <span key={t} className="tag" data-testid={`result-tag-${t}`}>{t.toUpperCase()}</span>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-4">
                <Link to={`/pro/${l.slug}`} className="btn-secondary text-xs py-2 px-3 flex-1 text-center" data-testid={`view-pro-${l.slug}`}>
                  View Profile <ArrowRight size={12} weight="bold" />
                </Link>
                <button onClick={() => setQuoteFor(l)} className="btn-primary text-xs py-2 px-3 flex-1" data-testid={`request-quote-${l.slug}`}>
                  Request Quote
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {quoteFor && (
        <QuoteFormModal listing={quoteFor.unmatched ? null : quoteFor} zip={zip || data?.origin?.zip || ""} onClose={() => setQuoteFor(null)} />
      )}
    </Layout>
  );
}
